import React from 'react';
import { motion } from 'framer-motion';
import { Star } from 'lucide-react';
interface Testimonial {
  initials: string;
  name: string;
  location: string;
  text: string;
  color: string;
  pack?: string;
}
interface TestimonialsProps {
  testimonials: Testimonial[];
  title?: string;
  className?: string;
}
export function Testimonials({
  testimonials,
  title,
  className = ''
}: TestimonialsProps) {
  return <section className={`py-16 bg-surface ${className}`} data-id="element-303">
      <div className="container mx-auto px-4" data-id="element-304">
        {title && <h2 className="font-bold text-center mb-12 text-white text-[18px]" data-id="element-305">
            {title}
          </h2>}

        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 max-w-6xl mx-auto" data-id="element-306">
          {testimonials.map((t, i) => <motion.div key={i} initial={{
          opacity: 0,
          y: 20
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true
        }} transition={{
          duration: 0.5,
          delay: i * 0.1
        }} className="bg-base/60 p-6 rounded-2xl shadow-sm border border-surface flex flex-col h-full" data-id="element-307">
              <div className="flex items-center gap-1 mb-4" data-id="element-308">
                {[...Array(5)].map((_, j) => <Star key={j} className="w-4 h-4 text-neon fill-neon" data-id="element-309" />)}
              </div>

              <p className="text-muted italic mb-6 flex-grow" data-id="element-310">"{t.text}"</p>

              <div className="flex items-center gap-3 mt-auto pt-4 border-t border-surface" data-id="element-311">
                <div className={`w-10 h-10 rounded-full flex items-center justify-center text-white font-bold text-sm ${t.color}`} data-id="element-312">
                  {t.initials}
                </div>
                <div data-id="element-313">
                  <p className="font-bold text-white text-sm" data-id="element-314">{t.name}</p>
                  <p className="text-xs text-muted" data-id="element-315">{t.location}</p>
                  {t.pack && <p className="text-xs font-medium text-electric mt-0.5" data-id="element-316">
                      {t.pack}
                    </p>}
                </div>
              </div>
            </motion.div>)}
        </div>
      </div>
    </section>;
}
export const initialTestimonials = [{
  initials: 'MG',
  name: 'María G.',
  location: 'Profesora de Física · Escuela Técnica, Córdoba',
  text: 'Llevaba años enseñando física con el capítulo de estructura atómica de un libro de 2009 que no mencionaba Argentina ni una sola vez. Abrí el Tomo 1 del Megapack y en una tarde tenía más material contextualizado en el programa nuclear argentino que en toda mi carrera docente. Ahora mis alumnos me preguntan sobre el CAREM-25, sobre Atucha II y sobre por qué Argentina exporta reactores al mundo, y tengo respuesta fundamentada para todo.',
  color: 'bg-purple-500'
}, {
  initials: 'RT',
  name: 'Rodrigo T.',
  location: 'Docente de Física y Química · Terciario, Neuquén',
  text: 'El Tomo 4 de Seguridad Nuclear y el análisis de Chernóbil me dieron el sistema que necesitaba para conectar la materia con la realidad histórica y tecnológica. Mis alumnos por primera vez entendieron la diferencia real entre un reactor nuclear y una bomba atómica, y dejaron de tener miedo irracional al tema. Lo implementé en dos semanas y el nivel de participación en el aula se multiplicó.',
  color: 'bg-pink-500'
}, {
  initials: 'SP',
  name: 'Sandra P.',
  location: 'Profesora de Física · Instituto de Formación Docente, Jujuy',
  text: 'Las actividades del Bono 1 las uso en cada clase práctica. Los ejercicios de decaimiento radiactivo con el ejemplo del Cs-137 de Chernóbil y los cálculos de producción de Embalse son exactamente lo que mis alumnos necesitaban: física nuclear real con contexto argentino. Nunca pensé que algo tan concreto iba a cambiar tanto mis clases.',
  color: 'bg-blue-500'
}];
export const moreTestimonials = [{
  initials: 'FM',
  name: 'Federico M.',
  location: '🏙️ Buenos Aires',
  pack: 'Megapack Energía Nuclear',
  text: 'Dos años enseñando física nuclear con el mismo capítulo genérico del manual. Con el Megapack en una tarde tenía el argumento pedagógico para explicar por qué Argentina usa uranio natural y Alemania no puede hacer lo mismo. Mis alumnos empezaron a preguntar sobre el CAREM-25 como si fuera algo suyo. Porque lo es.',
  color: 'bg-emerald-500'
}, {
  initials: 'RV',
  name: 'Rosa V.',
  location: '🏙️ Santa Fe',
  pack: 'Megapack Energía Nuclear',
  text: 'El Tomo 5 de Aplicaciones Nucleares me dio el enfoque perfecto para conectar la física con la vida cotidiana. Cuando explicás que el Tc-99m que diagnosticó el cáncer del abuelo de alguien fue producido a 35 km de Buenos Aires, en Ezeiza, los alumnos se enganchan solos. Mis clases cambiaron completamente.',
  color: 'bg-blue-500'
}, {
  initials: 'GN',
  name: 'Gabriela N.',
  location: '🏙️ Tucumán',
  pack: 'Megapack Energía Nuclear',
  text: 'Con el Bono de Casos de Estudio empecé a enseñar Chernóbil con los datos reales de la OMS. Cuando mis alumnos descubren que fueron 31 muertes directas y no millones, y que la evacuación de Fukushima mató más gente que la radiación, empieza un debate ciudadano profundo que ninguna otra materia genera.',
  color: 'bg-amber-500'
}, {
  initials: 'AS',
  name: 'Alejandro S.',
  location: '🏙️ Rosario',
  pack: 'Megapack Energía Nuclear',
  text: 'Cerré la planificación anual de Física Nuclear en dos horas usando los 6 Tomos como guía. Antes me llevaba semanas y siempre quedaba algún tema sin el contexto argentino. Ahora tengo todo el año organizado, con el CAREM-25, INVAP y las tres centrales argentinas integrados en cada unidad.',
  color: 'bg-slate-700'
}, {
  initials: 'SM',
  name: 'Sofía M.',
  location: '🏙️ Córdoba',
  pack: 'Megapack + Upsell Premium',
  text: 'El Upsell Premium cambió el nivel de mis clases en el terciario. Cuando les digo a mis alumnos que Microsoft reactivó Three Mile Island para alimentar sus centros de datos de IA, que hay empresas con miles de millones de dólares apostando a la fusión nuclear, y que el litio argentino es estratégico para el combustible de los reactores del futuro, les estoy enseñando el mundo en que van a trabajar.',
  color: 'bg-purple-600'
}];